/**
 * Cloud-mode `AssetClient`. Talks to the API's asset proxy:
 *
 *   GET  /v1/orgs/:slug/asset-origins/:id/assets
 *   POST /v1/orgs/:slug/asset-origins/:id/upload-url
 *
 * `list` is a single API round-trip. `upload` is two: ask for a
 * presigned PUT, then PUT the file directly to S3 (bandwidth bypasses
 * the Worker, which would otherwise hit the 100MB body cap).
 *
 * The free-mode counterpart (`localAssetClient` in `asset-origin.ts`)
 * signs in the browser. Both share the `AssetClient` interface so the
 * gallery doesn't branch on which one is in play.
 */

import type { AssetClient, AssetItem, ListResult } from './asset-origin.js';
import { AssetOriginError } from './asset-origin.js';

const DEFAULT_BASE = 'http://localhost:3001';

export interface CloudAssetClientOptions {
  /** Override the API base — falls back to `BLIKSYS_API_URL` then localhost. */
  baseUrl?: string;
  orgSlug: string;
  /** ID of the org-scoped `asset_origins` row this project links to. */
  originId: string;
}

export function cloudAssetClient(opts: CloudAssetClientOptions): AssetClient {
  const baseUrl = opts.baseUrl ?? import.meta.env.BLIKSYS_API_URL ?? DEFAULT_BASE;
  const root = `${baseUrl}/v1/orgs/${encodeURIComponent(opts.orgSlug)}/asset-origins/${encodeURIComponent(opts.originId)}`;

  return {
    async list(options = {}): Promise<ListResult> {
      const params = new URLSearchParams();
      if (options.continuationToken !== undefined) {
        params.set('continuationToken', options.continuationToken);
      }
      if (options.maxKeys !== undefined) {
        params.set('maxKeys', String(options.maxKeys));
      }
      const qs = params.toString();
      const res = await fetch(`${root}/assets${qs ? `?${qs}` : ''}`, {
        credentials: 'include',
      });
      if (!res.ok) throw await mapError(res);
      return (await res.json()) as ListResult;
    },

    async upload(filename: string, file: File | Blob): Promise<AssetItem> {
      const contentType = (file instanceof File && file.type) || 'application/octet-stream';
      // 1. Ask the server for a presigned PUT URL.
      const presignRes = await fetch(`${root}/upload-url`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: filename, contentType }),
      });
      if (!presignRes.ok) throw await mapError(presignRes);
      const presigned = (await presignRes.json()) as {
        uploadUrl: string;
        method: string;
        headers: Record<string, string>;
        key: string;
        publicUrl: string;
      };

      // 2. PUT directly to S3 (or whichever endpoint the presigned URL
      //    points at). Browser → bucket; the Worker is out of the path.
      const putRes = await fetch(presigned.uploadUrl, {
        method: presigned.method,
        headers: presigned.headers,
        body: file,
      });
      if (!putRes.ok) {
        throw new AssetOriginError(
          `Upload failed (${String(putRes.status)}). The bucket may need a CORS rule allowing PUT from this origin.`,
          'cors',
        );
      }

      return {
        key: presigned.key,
        size: file.size,
        url: presigned.publicUrl,
      };
    },
  };
}

async function mapError(res: Response): Promise<AssetOriginError> {
  if (res.status === 401) {
    return new AssetOriginError('Sign in required to access this asset origin.', 'auth');
  }
  let body: { code?: string; message?: string } = {};
  try {
    body = (await res.json()) as typeof body;
  } catch {
    /* fall through */
  }
  if (res.status === 404) {
    return new AssetOriginError(body.message ?? 'Asset origin not found.', 'not-found');
  }
  if (res.status === 403) {
    return new AssetOriginError(body.message ?? 'Access denied.', 'auth');
  }
  return new AssetOriginError(
    body.message ?? `Asset proxy returned ${String(res.status)}.`,
    'unknown',
  );
}
